// @/services/scm-permissions/index.js
import { permissionsStorage } from './storage';
import { privilegesService } from '@/services/permissions/privilegesService';

/**
 * SCM Permissions Service
 *
 * Parses Supply Chain Management permissions from privilegesService.
 * Does NOT make its own API calls - uses shared privileges data.
 *
 * Features:
 * - Zero API calls (uses privilegesService data)
 * - Synchronous permission checks
 * - Session storage caching
 * - Cross-tab sync via events
 */
class ScmPermissionsService {
  /**
   * Initialize the permission service with default state
   */
  constructor() {
    // Core state
    this.initialized = false;
    this.initializing = false;

    // Default permissions structure
    this.permissions = {
      canAccess: false,        // Can access SCM module
      canInvite: false,        // Can invite suppliers
      inviteQuota: 0,          // Total invitation quota
      remainingInvites: 0,     // Remaining invitations
      isTrialMode: false,      // Is in trial mode
      lastUpdated: null        // When permissions were last verified
    };

    // Flag to track parallel initialization
    this._initPromise = null;

    // Attempt to restore from storage immediately
    // If successful, mark as initialized so we don't show loading state
    if (this._restoreFromStorage()) {
      this.initialized = true;
    }

    // A privileges fetch that lands after we answered from fallback defaults
    // (a 401 on an expired token, retried after the token refresh) must
    // replace that answer, or the page stays on "not available".
    privilegesService.subscribe((privileges) => {
      if (!privileges || !privilegesService.inMemoryFetchedThisSession) return;
      this.permissions = {
        ...this.permissions,
        ...this._parsePrivileges(privileges),
        lastUpdated: new Date().toISOString()
      };
      this.initialized = true;
      permissionsStorage.savePermissions(this.permissions);
      if (typeof window !== 'undefined') {
        window.dispatchEvent(new CustomEvent('scm-permissions-changed'));
      }
    });
  }

  /**
   * Initialize SCM permissions from privilegesService (no API call)
   * @param {boolean} force - Force refresh from privilegesService
   * @returns {Promise<boolean>} Success status
   */
  async initialize(force = false) {
    if (this.initialized && !force) {
      return true;
    }

    if (this._initPromise) {
      return this._initPromise;
    }

    this._initPromise = this._performInitialization(force);

    try {
      const result = await this._initPromise;
      return result;
    } finally {
      this._initPromise = null;
      this.initializing = false;
    }
  }

  /**
   * Load permissions from privilegesService (NOT from API directly)
   * @private
   */
  async _performInitialization(force = false) {
    this.initializing = true;

    // Try cache first (page reload optimization)
    if (!force && this._restoreFromStorage()) {
      this.initialized = true;
      if (process.env.NODE_ENV === 'development') {
        console.log('[SCM Permissions] Restored from cache');
      }
      return true;
    }

    // Get privileges from privilegesService (which already fetched them)
    const success = await this._loadFromPrivilegesService(force);

    if (success) {
      this.initialized = true;
      // Only a real privileges answer is worth keeping for the session. A
      // fallback after a failed fetch would otherwise stick as "no access".
      if (privilegesService.inMemoryFetchedThisSession) {
        permissionsStorage.savePermissions(this.permissions);
      }
      if (process.env.NODE_ENV === 'development') {
        console.log('[SCM Permissions] Loaded from privilegesService:', {
          canAccess: this.permissions.canAccess,
          isTrialMode: this.permissions.isTrialMode
        });
      }
      return true;
    }

    // Fallback to defaults
    this._applyDefaultPermissions();
    this.initialized = true;
    if (process.env.NODE_ENV === 'development') {
      console.warn('[SCM Permissions] Using defaults (no privileges data)');
    }
    return false;
  }

  /**
   * Restore permissions from session storage
   * @private
   * @returns {boolean} True if restored successfully
   */
  _restoreFromStorage() {
    const stored = permissionsStorage.getPermissions();
    // A stored "no access" is always re-checked against privileges: older
    // builds saved it from the fallback after a 401 (ticket 1083 follow-up).
    if (stored && stored.canAccess) {
      this.permissions = { ...this.permissions, ...stored };
      return true;
    }
    return false;
  }

  /**
   * Apply conservative default permissions (no access)
   * @private
   */
  _applyDefaultPermissions() {
    this.permissions = {
      canAccess: false,
      canInvite: false,
      inviteQuota: 0,
      remainingInvites: 0,
      isTrialMode: false,
      lastUpdated: new Date().toISOString()
    };
  }

  /**
   * Load SCM permissions from privilegesService (no direct API call)
   * privilegesService already fetched /privileges during identity phase
   * @private
   * @param {boolean} force - Force refresh privilegesService first
   * @returns {Promise<boolean>} Success status
   */
  async _loadFromPrivilegesService(force = false) {
    try {
      // Get privileges from the shared service (already fetched, or will fetch once)
      const privileges = await privilegesService.getPrivileges(force);

      if (!privileges) {
        return false;
      }

      // Parse SCM-specific permissions from the shared privileges
      const parsedPermissions = this._parsePrivileges(privileges);

      this.permissions = {
        ...this.permissions,
        ...parsedPermissions,
        lastUpdated: new Date().toISOString()
      };

      return true;
    } catch (error) {
      if (process.env.NODE_ENV === 'development') {
        console.error('[SCM Permissions] Error parsing privileges:', error);
      }
      return false;
    }
  }

  /**
   * Parse privileges from API response
   * Converts raw API data to structured permissions
   * 
   * @private
   * @param {Object} privileges Privileges object from API
   * @returns {Object} Parsed permissions
   */
  _parsePrivileges(privileges) {
    // Default structure
    const parsed = {
      canAccess: false,
      canInvite: false,
      inviteQuota: 0,
      remainingInvites: 0,
      isTrialMode: false
    };

    // Check if SCM is in privileges
    if (privileges && privileges.SupplyChainManagement) {
      const scmAccess = privileges.SupplyChainManagement;

      // Parse based on format
      if (typeof scmAccess === 'string') {
        // Handle string format with both simple values and complex format
        if (scmAccess === 'GRANTED_PAID') {
          parsed.canAccess = true;
          parsed.canInvite = true;
          parsed.inviteQuota = 1000; // Assume large quota for paid
          parsed.remainingInvites = 1000;
        } else if (scmAccess === 'GRANTED_TRIAL') {
          parsed.canAccess = true;
          parsed.canInvite = true;
          parsed.inviteQuota = 10; // Default trial quota
          parsed.remainingInvites = 10;
          parsed.isTrialMode = true;
        } else if (scmAccess.includes('GRANTED_TRIAL')) {
          parsed.canAccess = true;
          parsed.canInvite = true;
          parsed.isTrialMode = true;

          // Try to extract quota
          const invitationsMatch = scmAccess.match(/Invitations:\s*(\d+)/i);
          if (invitationsMatch && invitationsMatch[1]) {
            const quota = parseInt(invitationsMatch[1], 10);
            parsed.inviteQuota = quota;
            parsed.remainingInvites = quota;
          } else {
            parsed.inviteQuota = 10; // Default if not specified
            parsed.remainingInvites = 10;
          }
        } else if (scmAccess.includes('GRANTED_PAID')) {
          parsed.canAccess = true;
          parsed.canInvite = true;

          // Try to extract quota
          const invitationsMatch = scmAccess.match(/Invitations:\s*(\d+)/i);
          if (invitationsMatch && invitationsMatch[1]) {
            const quota = parseInt(invitationsMatch[1], 10);
            parsed.inviteQuota = quota;
            parsed.remainingInvites = quota;
          } else {
            parsed.inviteQuota = 1000; // Default large quota for paid
            parsed.remainingInvites = 1000;
          }
        } else if (scmAccess === 'RESTRICTED_TRIAL_EXPIRED') {
          parsed.canAccess = false; // Can access but can't invite
        }
      } else if (scmAccess && typeof scmAccess === 'object') {
        // Handle object format
        if (scmAccess.permission === 'GRANTED_PAID') {
          parsed.canAccess = true;
          parsed.canInvite = true;
          parsed.inviteQuota = scmAccess.invitations || 1000;
          parsed.remainingInvites = scmAccess.invitations || 1000;
        } else if (scmAccess.permission === 'GRANTED_TRIAL') {
          parsed.canAccess = true;
          parsed.canInvite = true;
          parsed.inviteQuota = scmAccess.invitations || 10;
          parsed.remainingInvites = scmAccess.invitations || 10;
          parsed.isTrialMode = true;
        } else if (scmAccess.permission === 'RESTRICTED_TRIAL_EXPIRED') {
          parsed.canAccess = true; // Can access but can't invite
        }
      }
    }

    return parsed;
  }

  /**
   * Check if user can access SCM
   * Provides synchronous access check for immediate UI decisions
   * 
   * @returns {boolean} True if user can access SCM
   */
  canAccessSupplyChain() {
    return !!this.permissions.canAccess;
  }

  /**
   * Check if user can invite suppliers
   * Validates both access rights and available quota
   * 
   * @returns {boolean} True if user can invite suppliers
   */
  canInviteSuppliers() {
    return this.permissions.canAccess && this.permissions.canInvite && this.permissions.remainingInvites > 0;
  }

  /**
   * Get invitation quota
   * Returns the total allowed invitations
   * 
   * @returns {number} Invitation quota
   */
  getInviteQuota() {
    return this.permissions.inviteQuota || 0;
  }

  /**
   * Get remaining invitations
   * Returns the number of invitations still available
   * 
   * @returns {number} Remaining invitations
   */
  getRemainingInvites() {
    return this.permissions.remainingInvites || 0;
  }

  /**
   * Check if user is in trial mode
   * Determines if account has trial or paid status
   * 
   * @returns {boolean} True if user is in trial mode
   */
  isTrialMode() {
    return !!this.permissions.isTrialMode;
  }

  /**
   * Update supplier count and remaining invitations
   * Recalculates available invitations based on current suppliers
   * 
   * @param {number} supplierCount Current supplier count
   * @returns {void}
   */
  updateSupplierCount(supplierCount) {
    if (typeof supplierCount !== 'number' || supplierCount < 0) {
      return;
    }

    // Calculate remaining invitations
    const quota = this.permissions.inviteQuota || 0;
    this.permissions.remainingInvites = Math.max(0, quota - supplierCount);

    // Update storage
    permissionsStorage.savePermissions(this.permissions);

    // Dispatch an event so components can refresh
    if (typeof window !== 'undefined') {
      window.dispatchEvent(new CustomEvent('scm-permissions-changed'));
    }
  }

  /**
   * Decrement remaining invitations after successful invitation
   * Used to update quota client-side without API call
   * 
   * @returns {void}
   */
  decrementRemainingInvites() {
    if (this.permissions.remainingInvites > 0) {
      this.permissions.remainingInvites -= 1;

      // Update storage
      permissionsStorage.savePermissions(this.permissions);

      // Dispatch an event so components can refresh
      if (typeof window !== 'undefined') {
        window.dispatchEvent(new CustomEvent('scm-permissions-changed'));
      }
    }
  }

  /**
   * Refresh permissions from privilegesService
   * @param {boolean} force - Force refresh from API (default: false, uses cached privileges)
   * @returns {Promise<boolean>} Success status
   */
  async refreshPermissions(force = false) {
    try {
      // Re-parse SCM permissions from privilegesService data.
      // Default: use cached privileges (no API call). Pass force=true only when
      // privileges are known to have changed (e.g. first-run → open mode transition).
      const success = await this._loadFromPrivilegesService(force);

      if (success) {
        permissionsStorage.savePermissions(this.permissions);

        if (typeof window !== 'undefined') {
          window.dispatchEvent(new CustomEvent('scm-permissions-changed'));
        }

        if (process.env.NODE_ENV === 'development') {
          console.log('[SCM Permissions] Refreshed:', {
            canAccess: this.permissions.canAccess,
            remainingInvites: this.permissions.remainingInvites
          });
        }

        return true;
      }

      return false;
    } catch (error) {
      if (process.env.NODE_ENV === 'development') {
        console.error('[SCM Permissions] Refresh error:', error);
      }
      return false;
    }
  }

  /**
   * Clear permissions (useful for logout)
   * Resets permission state and removes from storage
   * 
   * @returns {void}
   */
  clearPermissions() {
    this.permissions = {
      canAccess: false,
      canInvite: false,
      inviteQuota: 0,
      remainingInvites: 0,
      isTrialMode: false,
      lastUpdated: null
    };

    permissionsStorage.clearPermissions();
    this.initialized = false;
  }
}

// Export singleton instance
export const scmPermissionsService = new ScmPermissionsService();